import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, CalendarClock, FolderArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";

const RELATIONS = ["Madre", "Padre", "Genitore", "Coniuge", "Figlio/a", "Nonno/a", "Fratello/Sorella", "Altro"];
const COLORS = ["#E07A5F", "#1E3A8A", "#3D8B6E", "#C9A227", "#7A5C99"];
const EMPTY = { name: "", relation: "Madre", birth_date: "", fiscal_code: "", notes: "", color: COLORS[0], linked_email: "" };

const ProfileDialog = ({ value, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (value) setF({ ...EMPTY, ...value });
  }, [value]);
  const save = async () => {
    const body = { ...f, birth_date: f.birth_date || null, fiscal_code: f.fiscal_code ? f.fiscal_code.toUpperCase() : null, linked_email: f.linked_email || null };
    delete body.link_status;
    try {
      if (value?.profile_id) await api.put(`/profiles/${value.profile_id}`, body);
      else await api.post("/profiles", body);
      toast.success("Profilo salvato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="profile-dialog">
        <DialogHeader><DialogTitle>{value?.profile_id ? "Modifica profilo" : "Nuovo familiare"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div><Label>Nome e cognome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Relazione</Label>
              <Select value={f.relation} onValueChange={(v) => setF({ ...f, relation: v })}>
                <SelectTrigger data-testid="profile-relation-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data di nascita</Label><Input type="date" value={f.birth_date || ""} onChange={(e) => setF({ ...f, birth_date: e.target.value })} data-testid="profile-birth-input" /></div>
          </div>
          <div><Label>Codice fiscale</Label><Input maxLength={16} value={f.fiscal_code || ""} onChange={(e) => setF({ ...f, fiscal_code: e.target.value })} data-testid="profile-cf-input" /></div>
          <div><Label>Note (medico, pensione, invalidità…)</Label><Textarea rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="profile-notes-input" /></div>
          <div><Label>Email del familiare (Modalità semplice, facoltativo)</Label><Input type="email" value={f.linked_email || ""} onChange={(e) => setF({ ...f, linked_email: e.target.value })} placeholder="mamma@esempio.it" data-testid="profile-linked-email-input" />
            <p className="mt-1 text-xs text-slate-500">Quando accede a Burocrazy con questa email e accetta, ricevi un avviso per ogni lettera che fotografa.</p></div>
          <div className="flex gap-2">{COLORS.map((c) => <button key={c} onClick={() => setF({ ...f, color: c })} className={`h-8 w-8 rounded-full border-2 ${f.color === c ? "border-slate-900" : "border-transparent"}`} style={{ background: c }} data-testid={`profile-color-${c.slice(1)}`} />)}</div>
        </div>
        <DialogFooter><Button disabled={!f.name} onClick={save} data-testid="profile-save-btn">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const age = (d) => (d ? Math.floor((Date.now() - new Date(d)) / 31557600000) : null);

export default function Family() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/profiles").then(({ data }) => setItems(data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const remove = async (p) => {
    if (!window.confirm(`Eliminare il profilo di ${p.name}? Documenti e scadenze resteranno nel tuo account.`)) return;
    await api.delete(`/profiles/${p.profile_id}`);
    load();
  };

  return (
    <div data-testid="family-page">
      <PageHeader eyebrow="Profili famiglia" title="Le persone che segui" desc="Genitori anziani, figli, coniuge: ognuno con le sue pratiche e scadenze.">
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="family-add-btn"><Plus className="h-4 w-4 mr-2" /> Aggiungi familiare</Button>
      </PageHeader>
      {items.length === 0 && (
        <div className="rounded-3xl border bg-white p-12 text-center" data-testid="family-empty">
          <p className="font-display text-xl">Nessun familiare ancora.</p>
          <p className="text-slate-500 mt-1">Aggiungi ad esempio mamma o papà per gestire le loro pratiche.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.profile_id} className="lift rounded-3xl border bg-white p-6" data-testid={`profile-card-${p.profile_id}`}>
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 rounded-2xl grid place-items-center text-white font-display text-lg font-semibold" style={{ background: p.color }}>{p.name[0]}</span>
              <div><p className="font-medium text-lg">{p.name}</p><p className="text-sm text-slate-500">{p.relation}{p.birth_date ? ` · ${age(p.birth_date)} anni` : ""}</p></div>
            </div>
            {p.fiscal_code && <p className="mt-4 text-xs font-mono text-slate-500">CF {p.fiscal_code}</p>}
            {p.linked_email && <p className="mt-2 text-xs" data-testid={`profile-link-status-${p.profile_id}`}>{p.link_status === "accepted" ? "Collegato: ricevi i suoi avvisi" : "In attesa che accetti"} · {p.linked_email}</p>}
            {p.notes && <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.notes}</p>}
            {p.birth_date && <p className="mt-2 text-xs text-slate-400">Nato/a il {fmtDate(p.birth_date)}</p>}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline" className="rounded-full"><Link to="/app/scadenze"><CalendarClock className="h-4 w-4 mr-1" /> Scadenze</Link></Button>
              <Button asChild size="sm" variant="outline" className="rounded-full"><Link to="/app/archivio"><FolderArchive className="h-4 w-4 mr-1" /> Documenti</Link></Button>
              <button onClick={() => setEdit(p)} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`profile-edit-${p.profile_id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(p)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`profile-delete-${p.profile_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <ProfileDialog value={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
